import React from 'react';
import { ArrowLeft, User, Mail, Phone, Download } from 'lucide-react';
import { Logo } from './Logo';
import { Link } from './Router';
import { PageHeader } from './PageHeader';

interface LinktreeProps {
  profileId?: string;
}

export const Linktree: React.FC<LinktreeProps> = ({ profileId }) => {
  const profiles = [
    {
      id: 'edin',
      firstName: 'Edin',
      lastName: 'Becirovic',
      role: 'Sales & Ansprechpartner',
      email: 'edin@zackig.marketing',
      phone: '+49 173 189 88 04',
    },
    {
      id: 'arne',
      firstName: 'Arne',
      lastName: 'Kabelitz',
      role: 'Marketing & Content',
      email: 'arne@zackig.marketing',
      phone: '+49 152 224 238 53',
    },
    {
      id: 'hakan',
      firstName: 'Hakan',
      lastName: 'Er',
      role: 'Geschäftsführer',
      email: 'hakan@zackig.marketing',
      phone: '+49 176 206 008 34',
    }
  ];

  // If a profileId is passed, we filter. If not, we show all (Directory mode)
  const activeProfiles = profileId 
    ? profiles.filter(p => p.id === profileId)
    : profiles;

  // Helper to construct vCard data dynamically
  const getVCardData = (profile: typeof profiles[0]) => {
    return `BEGIN:VCARD
VERSION:3.0
N:${profile.lastName};${profile.firstName};;;
FN:${profile.firstName} ${profile.lastName}
ORG:zackig! Marketing
TITLE:${profile.role}
${profile.phone ? `TEL;TYPE=CELL:${profile.phone.replace(/\s/g, '')}` : ''}
EMAIL:${profile.email}
URL:https://www.zackig.marketing
END:VCARD`;
  };

  const downloadVCard = (profile: typeof profiles[0]) => {
    const data = getVCardData(profile);
    const blob = new Blob([data], { type: 'text/vcard' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${profile.firstName}_${profile.lastName}.vcf`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // --- SINGLE PROFILE VIEW (Business Card Mode) ---
  if (profileId && activeProfiles.length === 1) {
    const profile = activeProfiles[0];
    return (
      <div className="min-h-screen bg-[#fafafa] flex items-center justify-center p-4">
        <div className="w-full max-w-md bg-white rounded-[2.5rem] shadow-2xl overflow-hidden border border-gray-100 relative">
          
          {/* Header/Cover */}
          <div className="bg-[#111] p-8 pb-12 relative text-center">
             <div className="absolute top-0 right-0 w-64 h-64 bg-brand-orange rounded-full blur-[80px] opacity-20 -translate-y-1/2 translate-x-1/3 pointer-events-none"></div>
             
             {/* Back Button */}
             <div className="absolute top-6 left-6 z-20">
                <Link to="/" className="w-10 h-10 bg-white/10 backdrop-blur-md rounded-full flex items-center justify-center text-white hover:bg-brand-orange transition-colors">
                   <ArrowLeft size={18} />
                </Link>
             </div>

             <div className="relative z-10 flex flex-col items-center mt-8">
                <div className="w-32 h-32 p-1.5 rounded-full bg-gradient-to-b from-brand-orange to-[#111] mb-6 shadow-2xl">
                    <div className="w-full h-full rounded-full bg-[#111] flex items-center justify-center border-4 border-[#111] overflow-hidden">
                         <span className="text-4xl font-black text-white">{profile.firstName.charAt(0)}{profile.lastName.charAt(0)}</span>
                    </div>
                </div>
                <h1 className="text-3xl font-black text-white tracking-tight uppercase mb-2">
                  {profile.firstName} <br/> {profile.lastName}
                </h1>
                <div className="h-1 w-12 bg-brand-orange rounded-full mb-4"></div>
                <p className="text-white/60 text-xs font-bold uppercase tracking-[0.2em]">{profile.role}</p>
             </div>
          </div>

          {/* Links Body */}
          <div className="px-6 py-8 -mt-8 relative z-20 bg-white rounded-t-[2.5rem] flex flex-col gap-4">
             
             <button 
                onClick={() => downloadVCard(profile)}
                className="w-full bg-brand-orange text-white font-bold uppercase tracking-widest text-sm py-5 rounded-2xl flex items-center justify-center gap-3 hover:bg-orange-600 transition-all shadow-lg shadow-orange-500/30 mb-4"
             >
                <Download size={20} />
                Kontakt speichern
             </button>

             <a 
                href={`tel:${profile.phone.replace(/\s/g, '')}`}
                className="w-full bg-gray-50 text-brand-dark font-bold p-5 rounded-2xl flex items-center gap-4 hover:bg-gray-100 transition-colors border border-gray-100 group"
             >
                <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-sm text-brand-orange group-hover:scale-110 transition-transform">
                   <Phone size={20} />
                </div>
                <div className="flex-grow text-left">
                   <span className="block text-[10px] text-gray-400 uppercase tracking-wider font-bold">Mobil</span>
                   <span className="text-lg">{profile.phone}</span>
                </div>
             </a>

             <a 
                href={`mailto:${profile.email}`}
                className="w-full bg-gray-50 text-brand-dark font-bold p-5 rounded-2xl flex items-center gap-4 hover:bg-gray-100 transition-colors border border-gray-100 group"
             >
                <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-sm text-brand-orange group-hover:scale-110 transition-transform">
                   <Mail size={20} />
                </div>
                <div className="flex-grow text-left">
                   <span className="block text-[10px] text-gray-400 uppercase tracking-wider font-bold">Email</span>
                   <span className="text-sm md:text-base break-all">{profile.email}</span>
                </div>
             </a>

             {/* QR Code / Agentur Button Replacement */}
             <button 
                onClick={() => downloadVCard(profile)}
                className="w-full bg-[#111] p-5 rounded-2xl flex items-center justify-between gap-4 shadow-xl overflow-hidden relative group hover:scale-[1.02] transition-transform duration-300"
             >
                {/* Background Decor */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-brand-orange/10 rounded-full blur-2xl translate-x-1/2 -translate-y-1/2 group-hover:bg-brand-orange/20 transition-colors"></div>
                
                <div className="flex flex-col pl-2 relative z-10 text-left">
                   <span className="text-brand-orange font-black uppercase tracking-widest text-xs mb-1">Scan Me</span>
                   <span className="text-white font-bold text-lg leading-tight">Kontakt<br/>speichern</span>
                </div>

                <div className="bg-white p-2 rounded-xl relative z-10 shadow-lg transform group-hover:scale-110 transition-transform duration-300">
                   <img 
                      src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&format=svg&bgcolor=ffffff&color=000000&data=${encodeURIComponent(getVCardData(profile))}`} 
                      alt="Contact QR" 
                      className="w-20 h-20 md:w-24 md:h-24"
                   />
                </div>
             </button>

             <div className="mt-8 text-center opacity-30">
                <Link to="/">
                   <Logo variant="dark" className="h-6 mx-auto hover:opacity-100 transition-opacity" />
                </Link>
             </div>

          </div>
        </div>
      </div>
    );
  }

  // --- DIRECTORY VIEW (If no ID is passed) ---
  return (
    <div className="min-h-screen bg-brand-gray font-sans selection:bg-brand-orange selection:text-white animate-fade-in-up">
      
      {/* Standard Header */}
      <PageHeader title="Unser//Team" subtitle="Crew" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-12 text-left">
            <p className="text-gray-500 font-medium text-lg max-w-2xl">Wähle deinen Ansprechpartner für direkten Kontakt.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12">
          {activeProfiles.map((profile) => (
            <div key={profile.id} className="bg-white rounded-[3rem] shadow-[0_20px_50px_-12px_rgba(0,0,0,0.1)] overflow-hidden border border-gray-100 flex flex-col hover:-translate-y-2 transition-transform duration-500 min-h-[500px] relative group">
              
              {/* Card Header */}
              <div className="bg-[#0a0a0a] p-10 pb-16 text-center relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-64 h-64 bg-brand-orange rounded-full blur-[80px] opacity-20 -translate-y-1/2 translate-x-1/3"></div>
                  
                  <div className="relative z-10 flex flex-col items-center">
                      <div className="w-24 h-24 p-1 rounded-full bg-gradient-to-b from-brand-orange to-[#0a0a0a] mb-6 shadow-2xl">
                          <div className="w-full h-full rounded-full bg-[#111] flex items-center justify-center border-2 border-[#111] overflow-hidden">
                              <User size={40} className="text-white" />
                          </div>
                      </div>
                      <h2 className="text-2xl font-black text-white tracking-tight mb-2 uppercase">{profile.firstName} <br/> {profile.lastName}</h2>
                      <div className="h-1 w-12 bg-brand-orange rounded-full mb-4"></div>
                      <p className="text-gray-400 text-[10px] font-bold uppercase tracking-[0.2em]">{profile.role}</p>
                  </div>
              </div>

              {/* Card Content */}
              <div className="px-8 -mt-8 relative z-20 pb-10 flex-grow flex flex-col gap-4">
                  
                  {/* Link to specific page */}
                  <Link 
                      to={`/${profile.id}`} 
                      className="w-full bg-brand-orange hover:bg-orange-600 text-white p-4 rounded-2xl shadow-lg shadow-orange-500/30 flex items-center justify-center gap-2 group/btn transition-all duration-300 hover:scale-[1.02] font-bold uppercase tracking-wide text-sm"
                  >
                      KONTAKT EINSPEICHERN
                  </Link>

                  {/* Quick Actions */}
                  <div className="grid grid-cols-2 gap-3 mt-2">
                      <a 
                        href={`mailto:${profile.email}`} 
                        className="flex flex-col items-center justify-center gap-2 bg-gray-50 hover:bg-gray-100 p-4 rounded-2xl transition-all border border-gray-100 hover:border-gray-200 group/item"
                      >
                          <Mail size={20} className="text-brand-dark group-hover/item:text-brand-orange transition-colors" />
                          <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider group-hover/item:text-brand-dark">Email</span>
                      </a>
                      <a 
                        href={`tel:${profile.phone.replace(/\s/g, '')}`} 
                        className="flex flex-col items-center justify-center gap-2 bg-gray-50 hover:bg-gray-100 p-4 rounded-2xl transition-all border border-gray-100 hover:border-gray-200 group/item"
                      >
                          <Phone size={20} className="text-brand-dark group-hover/item:text-brand-orange transition-colors" />
                          <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider group-hover/item:text-brand-dark">Call</span>
                      </a>
                  </div>

                  {/* QR Code */}
                  <div className="mt-4 pt-6 border-t border-gray-50 flex flex-col items-center justify-center gap-3">
                      <div className="bg-white p-3 rounded-xl border border-gray-100 shadow-sm">
                        <img 
                          src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&format=svg&data=${encodeURIComponent(`https://zackig.marketing/${profile.id}`)}`} 
                          alt={`QR Code ${profile.firstName}`} 
                          className="w-24 h-24"
                        />
                      </div>
                      <span className="text-[10px] font-bold text-gray-300 uppercase tracking-widest">SCANNEN ZUM SPEICHERN</span>
                  </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};